import {useState} from 'react'
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import useGuideStore from '../../store/useGuideStore';
import { Chip, IconButton } from '@mui/material'
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import MenuIcon from '@mui/icons-material/Menu'

const SIDEBAR_WIDTH = 240;

export default function Sidebar() {
  const {
    index,
    indexLoading,
    indexError,
    activeGuideId,
    setActiveGuideId,
      themeMode,
      toggleTheme
  } = useGuideStore();

  // Group guides by category
  const categories = index.reduce<Record<string, typeof index>>((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
    const [open, setOpen] = useState(false)

    const toggleCategory = (category: string) => {
        setOpenCategories((prev) => ({ ...prev, [category]: !prev[category] }));
    }

    const toggleDrawer = (newOpen: boolean) => () => {
        setOpen(newOpen)
    }

  return (
      <div>
          <IconButton aria-label="open Drawer" onClick={toggleDrawer(true)}>
              <MenuIcon />
          </IconButton>
    <Drawer
        open={open}
        onClose={toggleDrawer(false)}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* App title */}
      <Box sx={{ px: 3, py: 3,
          borderBottom: '2px solid', borderColor: 'divider' }}>
        <Typography
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.625rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          Personal Knowledge
        </Typography>
        <Typography variant="h2" sx={{ fontSize: '1.125rem' }}>
          Guides
        </Typography>
      </Box>

      {/* Guide list */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {indexLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={20} sx={{ color: '#0a0a0a' }} />
          </Box>
        )}

        {indexError && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Typography variant="caption" color="error">
              {indexError}
            </Typography>
          </Box>
        )}

        {!indexLoading && !indexError && Object.entries(categories).map(([category, entries]) => (
          <Box key={category}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
                <Chip
                    label= {category}
                    onClick={() => toggleCategory(category)}
                    icon={openCategories[category] ? <ArrowDownwardIcon /> : <ArrowUpwardIcon /> }
                    size={'small'}
                    sx={{
                        fontFamily: '"IBM Plex Mono", monospace',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        fontWeight: 900,
                        color: 'text.primary',
                        borderRadius: '20px'
                    }}
                />
            </Box>
              {openCategories[category] && (
            <List dense disablePadding>
              {entries.map((entry) => (
                <ListItemButton
                  key={entry.id}
                  selected={activeGuideId === entry.id}
                  onClick={() => setActiveGuideId(entry.id)}
                  sx={{
                    px: 3,
                    py: 1.25,
                      borderLeft: activeGuideId === entry.id ? '3px solid' : '3px solid transparent',
                      borderLeftColor: activeGuideId === entry.id ? 'text.primary' : 'transparent',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: activeGuideId === entry.id ? 600 : 400,
                        color: activeGuideId === entry.id ? 'primary.contrastText' : 'text.primary',
                        lineHeight: 1.4,
                      }}
                    >
                      {entry.title}
                    </Typography>
                  </Box>
                </ListItemButton>
              ))}
            </List>
              )}
            <Divider sx={{ mt: 1 }} />
          </Box>
        ))}
      </Box>

      {/* Footer */}
        <Box
            sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Typography
                sx={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '0.625rem',
                    color: 'text.secondary',
                    letterSpacing: '0.06em',
                }}
            >
                {index.length} guide{index.length !== 1 ? 's' : ''} loaded
            </Typography>

            <IconButton onClick={toggleTheme} size="small" sx={{ color: 'text.secondary' }}>
                {themeMode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
        </Box>
    </Drawer>
      </div>
  );
}
